import { FC, Ref, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import { faBooks } from '@fortawesome/pro-solid-svg-icons';
import {
  Button,
  createBemBlockBuilder,
  Loading,
  TextArea,
} from '@ebsco-ui/ebsco-ui';

import {
  Dropdown,
  DropdownItem,
  FeatureFlagDisplay,
  getErrorReason,
  HoldingAvailability,
  HoldsAndCheckoutsMessage,
  Icon,
  Modal,
  ModalNotificationType,
  RedirectToLoginButton,
} from '@app/components';
import {
  useAppContext,
  useAvailabilityContext,
  useMappingsContext,
  useRecordSummaryContext,
} from '@app/contexts';
import {
  queryKeys,
  useFeatureFlag,
  useHoldsAndCheckoutsPageConfig,
} from '@app/hooks';
import { wrapBoldTranslationChunks } from '@app/utils';
import { sharedMessages } from '@app/translations';
import { Feature, NotificationStatus, Route } from '@app/constants';

import { InactiveAccountNotification } from '../InactiveAccountNotification';
import { PlaceHoldCopies } from './PlaceHoldCopies';
import { usePlaceHoldCopies } from './usePlaceHoldCopies';
import { RecordSummaryModalHeader } from '../RecordSummaryModalHeader';
import { useAllowedServicePointsFetch } from './useAllowedServicePointsFetch';
import { usePlaceHoldMutation } from './usePlaceHoldMutation';
import { PlaceHoldNotification } from './PlaceHoldNotification';

import '../Modal.scss';
import './PlaceHoldModal.scss';

interface PlaceHoldModalProps {
  isOpen: boolean;
  onModalClose: () => void;
}

const cnBemModal = createBemBlockBuilder(['modal']);
const cnBem = createBemBlockBuilder(['placeHoldModal']);

export const PlaceHoldModal: FC<PlaceHoldModalProps> = ({
  isOpen,
  onModalClose,
}) => {
  const { $t } = useIntl();
  const queryClient = useQueryClient();
  const { isLoggedIn } = useAppContext();
  const patronCommentsRef = useRef<HTMLTextAreaElement>();
  const {
    copies,
    copyType,
    selectedCopy,
    setSelectedCopy,
    shouldDisplayCopiesDropdown,
    initialCopyType,
    setCopyType,
  } = usePlaceHoldCopies({ isOpen });
  const { userItemStatuses } = useMappingsContext();
  const { isActive: hasLibraryNote, config: libraryNote } = useFeatureFlag({
    name: Feature.placeHoldLibraryNote,
  });
  const { selectConfiguredOption } = useHoldsAndCheckoutsPageConfig();
  const [locations, setLocations] = useState<DropdownItem[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<DropdownItem | null>(
    null
  );
  const [selectedLibrary, setSelectedLibrary] = useState<DropdownItem | null>(
    null
  );
  const [status, setStatus] = useState<ModalNotificationType | null>(null);
  const { isAvailabilityLoading, getAvailability } = useAvailabilityContext();
  const placeHoldMutation = usePlaceHoldMutation();
  const { instance } = useRecordSummaryContext();
  const isUserAccountActive = userItemStatuses?.isActive !== false;
  const locationsQuery = useAllowedServicePointsFetch({
    id: instance.id,
    shouldFetch: isOpen && isEmpty(locations) && isUserAccountActive,
  });
  const locationEmptyOption = useMemo(
    () => ({
      label: $t({
        id: 'placeHoldModal.selectLocation',
        defaultMessage: 'Select location',
      }),
    }),
    [$t]
  );
  const shouldDisplayLibrariesDropdown =
    locationsQuery.data?.isPickupAnywhere &&
    !isEmpty(locationsQuery.data?.libraries);
  const titleMessage = selectConfiguredOption(
    $t(sharedMessages.requestItem),
    $t(sharedMessages.placeHold)
  );

  const resetRelayOnModalValues = () => {
    setCopyType(initialCopyType);
    setSelectedCopy(null);
    setSelectedLocation(null);
    setStatus(null);
    updateSelectedLibrary();
  };

  const updateSelectedLibrary = (value?: DropdownItem) => {
    if (locationsQuery.data?.isPickupAnywhere) {
      const { allowedServicePoints, libraries, homeLibraryId } =
        locationsQuery.data;

      const library =
        value ??
        libraries.find(({ id }) => id === homeLibraryId) ??
        libraries[0];

      setSelectedLibrary(library);
      setSelectedLocation(null);
      setLocations([
        locationEmptyOption,
        ...allowedServicePoints.filter(
          ({ libraryId }) => libraryId === library.id
        ),
      ]);
    }
  };

  useEffect(() => {
    if (locationsQuery.data) {
      const { allowedServicePoints, isPickupAnywhere } = locationsQuery.data;

      if (isPickupAnywhere) {
        updateSelectedLibrary();
      } else {
        setLocations([locationEmptyOption, ...allowedServicePoints]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationsQuery.data]);

  useEffect(() => {
    if (
      placeHoldMutation.status === NotificationStatus.success ||
      placeHoldMutation.status === NotificationStatus.error
    ) {
      setStatus(placeHoldMutation.status);
    }

    if (placeHoldMutation.status === NotificationStatus.success) {
      getAvailability([instance.id]);
      queryClient.refetchQueries([queryKeys.userItemStatuses], {
        type: 'active',
      });
    }
  }, [queryClient, getAvailability, placeHoldMutation.status, instance.id]);

  useEffect(() => {
    setSelectedLocation(null);
  }, [selectedCopy?.id]);

  const getModalBody = () => {
    if (!isLoggedIn) {
      return (
        <HoldsAndCheckoutsMessage
          requestsMessage={$t({
            id: 'placeHoldModal.signInOrCreateAccountToRequest',
            defaultMessage: 'To request an item, sign in.',
          })}
          holdsMessage={$t({
            id: 'placeHoldModal.signInOrCreateAccount',
            defaultMessage: 'To place a hold, sign in.',
          })}
        />
      );
    }

    if (!isUserAccountActive) {
      return <InactiveAccountNotification />;
    }

    return status ? (
      <PlaceHoldNotification
        type={status}
        queuePosition={placeHoldMutation?.data?.queuePosition}
        errorMessage={getErrorReason(placeHoldMutation, $t)}
      />
    ) : (
      <form>
        {hasLibraryNote && libraryNote?.note && (
          <div className={cnBem('__libraryNote')} data-auto="library-note">
            {$t(
              {
                id: 'placeHoldModal.note',
                defaultMessage: '<b>Note:</b> {note}',
              },
              {
                b: wrapBoldTranslationChunks,
                note: libraryNote.note,
              }
            )}
          </div>
        )}
        <div className={cnBem('__formContainer')}>
          <PlaceHoldCopies
            copyType={copyType}
            copies={copies}
            selectedCopy={selectedCopy}
            shouldDisplayCopiesDropdown={shouldDisplayCopiesDropdown}
            setCopyType={setCopyType}
            setSelectedCopy={setSelectedCopy}
          />
          {!locationsQuery.isError && (
            <>
              {shouldDisplayLibrariesDropdown && (
                <Dropdown
                  label={$t({
                    id: 'placeHoldModal.choosePickupLibrary',
                    defaultMessage: 'Choose a pickup library',
                  })}
                  items={locationsQuery.data?.libraries}
                  id="place-hold-choose-library"
                  styleType="standard"
                  selectedItem={selectedLibrary as DropdownItem}
                  onSelectedItemChange={({ selectedItem }) => {
                    updateSelectedLibrary(selectedItem as DropdownItem);
                  }}
                />
              )}
              <Dropdown
                label={$t({
                  id: 'placeHoldModal.choosePickupLocation',
                  defaultMessage: 'Choose a pickup location',
                })}
                items={locations}
                id="place-hold-choose-location"
                styleType="standard"
                selectedItem={
                  (selectedLocation as DropdownItem) ?? locationEmptyOption
                }
                isLoading={locationsQuery.isLoading}
                onSelectedItemChange={({ selectedItem }) => {
                  setSelectedLocation(selectedItem as DropdownItem);
                }}
              />
            </>
          )}
          <FeatureFlagDisplay flagToMatch={{ name: Feature.placeHoldNotes }}>
            <TextArea
              id="patron-comments"
              ref={patronCommentsRef as Ref<HTMLTextAreaElement>}
              label={$t({
                id: 'placeHoldModal.notes',
                defaultMessage: 'Notes (optional)',
              })}
            />
          </FeatureFlagDisplay>
        </div>
      </form>
    );
  };

  const getModalFooter = () => {
    if (!isLoggedIn) {
      return (
        <RedirectToLoginButton
          redirectBackRoute={`${generatePath(Route.instanceDetails, {
            id: instance.id,
          })}?isPlaceHold=true`}
        />
      );
    }

    return (
      <>
        <Button styleType="text" onClick={onModalClose}>
          <FormattedMessage
            {...(status ? sharedMessages.close : sharedMessages.cancel)}
          />
        </Button>
        {!status && (
          <Button
            styleType="default"
            disabled={!selectedLocation?.id || !isUserAccountActive}
            onClick={() => {
              if (selectedLocation?.id) {
                placeHoldMutation.mutate({
                  instanceId: instance.id,
                  pickupLocationId: selectedLocation.id,
                  item: selectedCopy as HoldingAvailability,
                  patronComments: patronCommentsRef.current?.value,
                });
              }
            }}
          >
            <Icon icon={faBooks} />
            {titleMessage}
          </Button>
        )}
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      modalTitle={titleMessage}
      onClose={onModalClose}
      onAfterClose={resetRelayOnModalValues}
    >
      <div
        className={cn({
          [cnBemModal('__disabledScroll')]: placeHoldMutation.isLoading,
          [cnBemModal('--is-logged-out')]: !isLoggedIn,
        })}
      >
        <RecordSummaryModalHeader
          headerTitle={titleMessage}
          onModalClose={onModalClose}
        />
        <Modal.Body>{getModalBody()}</Modal.Body>
        <Modal.Footer>{getModalFooter()}</Modal.Footer>
        {(placeHoldMutation.isLoading ||
          (isAvailabilityLoading && status === null)) && (
          <Loading
            loadingMessage={
              isAvailabilityLoading
                ? $t(sharedMessages.loadingMessage)
                : selectConfiguredOption(
                    $t({
                      id: 'placeHoldModal.requestingItem',
                      defaultMessage: 'Requesting item...',
                    }),
                    $t({
                      id: 'placeHoldModal.placingHold',
                      defaultMessage: 'Placing hold...',
                    })
                  )
            }
          />
        )}
      </div>
    </Modal>
  );
};
